import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Calendar,
  Clock,
  Package,
  BookOpen,
  FileText,
  FolderOpen,
  Settings,
  Users,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Shield,
  Menu,
  X,
  Bell,
  ClipboardList,
  DollarSign,
  Building2,
  Wallet
} from 'lucide-react';

const navItems = [
  { 
    label: 'Dashboard', 
    path: '/dashboard', 
    icon: LayoutDashboard,
    roles: ['admin', 'employee', 'physician']
  },
  { 
    label: 'Schedules', 
    path: '/schedules', 
    icon: Calendar,
    roles: ['admin', 'employee', 'physician'],
    children: [
      { label: 'Physician Schedule', path: '/schedules/physician', roles: ['admin', 'employee', 'physician'] },
      { label: 'Employee Schedule', path: '/schedules/employee', roles: ['admin', 'employee', 'physician'] }
    ]
  },
  { 
    label: 'Timesheets', 
    path: '/timesheets', 
    icon: Clock,
    roles: ['admin', 'employee']
  },
  { 
    label: 'Supplies', 
    path: '/supplies', 
    icon: Package,
    roles: ['admin', 'employee', 'physician']
  },
  { 
    label: 'Training', 
    path: '/training', 
    icon: BookOpen,
    roles: ['admin', 'employee', 'physician']
  },
  { 
    label: 'Payroll Documents', 
    path: '/payroll', 
    icon: FileText,
    roles: ['admin', 'employee']
  },
  { 
    label: 'Employee Onboarding', 
    path: '/onboarding', 
    icon: FolderOpen,
    roles: ['admin', 'employee']
  },
  {
    label: 'Admin Panel',
    path: '/admin',
    icon: Settings,
    roles: ['admin'],
    children: [
      { label: 'Operations Dashboard', path: '/admin/operations', roles: ['admin'] },
      { label: 'Finance', path: '/admin/finance', roles: ['admin'] },
      { label: 'User Management', path: '/admin/users', roles: ['admin'] },
      { label: 'Schedule Templates', path: '/admin/schedule-templates', roles: ['admin'] },
      { label: 'Pay Periods', path: '/admin/pay-periods', roles: ['admin'] },
      { label: 'Payroll Calculator', path: '/payroll/calculator', roles: ['admin'] },
      { label: 'YTD Management', path: '/admin/ytd-management', roles: ['admin'] },
      { label: 'CRA Remittance', path: '/admin/cra-remittance', roles: ['admin'] },
      { label: 'Onboarding Status', path: '/admin/onboarding-status', roles: ['admin'] },
      { label: 'Training Compliance', path: '/admin/training-compliance', roles: ['admin'] },
      { label: 'Supply Catalog', path: '/admin/supply-catalog', roles: ['admin'] },
      { label: 'Operations Assistant', path: '/admin/assistant', roles: ['admin'] },
      { label: 'Document Management', path: '/admin/documents', roles: ['admin'] },
      { label: 'Audit Logs', path: '/admin/audit-logs', roles: ['admin'] }
    ]
  }
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(['Schedules', 'Admin Panel']);

  const toggleExpand = (label) => {
    setExpandedItems(prev => 
      prev.includes(label) 
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const filterByRole = (items) => {
    return items.filter(item => item.roles.includes(user?.role));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitials = user 
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : 'U';

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'employee': return 'bg-blue-100 text-blue-700';
      case 'physician': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#000053' }}>
              <Shield className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Polar iHealth</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="mobile-user-menu">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-sm" style={{ backgroundColor: 'rgba(0, 109, 115, 0.15)', color: '#006D73' }}>
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2">
              <p className="font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="mobile-profile-link">
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="mobile-logout-btn">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-20' : 'w-72'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:top-0
        `}
      >
        {/* Sidebar Header */}
        <div className={`h-16 flex items-center border-b border-slate-200 ${collapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
          {!collapsed && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: '#000053' }}>
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900 text-sm">Polar iHealth</h1>
                <p className="text-xs text-slate-500">Internal Portal</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#000053' }}>
              <Shield className="h-5 w-5 text-white" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex h-8 w-8"
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto sidebar-scroll" style={{ height: 'calc(100vh - 140px)' }}>
          {filterByRole(navItems).map((item) => (
            <div key={item.path}>
              {item.children ? (
                <>
                  <button
                    onClick={() => !collapsed && toggleExpand(item.label)}
                    className={`sidebar-link w-full ${isActive(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronRight 
                          className={`h-4 w-4 transition-transform ${expandedItems.includes(item.label) ? 'rotate-90' : ''}`}
                        />
                      </>
                    )}
                  </button>
                  {!collapsed && expandedItems.includes(item.label) && (
                    <div className="ml-4 pl-4 border-l border-slate-200 space-y-1 mt-1">
                      {filterByRole(item.children).map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setMobileOpen(false)}
                          className={`sidebar-link text-sm ${isActive(child.path) ? 'active' : ''}`}
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span>{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer - User */}
        <div className={`absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white ${collapsed ? 'px-2' : ''}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors ${collapsed ? 'justify-center' : ''}`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-teal-100 text-teal-700 font-medium">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role}
                    </span>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                <User className="mr-2 h-4 w-4" />
                Profile & Security
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`
          transition-all duration-300 ease-in-out
          pt-16 lg:pt-0
          ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}
        `}
      >
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-white border-b border-slate-200 items-center justify-between px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <nav className="flex items-center gap-2 text-sm text-slate-500">
              <Link to="/dashboard" className="hover:text-slate-900">Home</Link>
              {location.pathname !== '/dashboard' && (
                <>
                  <span>/</span>
                  <span className="text-slate-900 font-medium">
                    {location.pathname.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
